import React, { useState } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Drawer,
  Fab,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ListAlt as ListAltIcon } from '@mui/icons-material';

export interface SidebarSectionItem {
  id: string;
  label: string;
}

export interface SidebarGroup {
  label: string;
  items: SidebarSectionItem[];
  hidden?: boolean;
}

export interface SidebarStandaloneItem {
  id: string;
  label: string;
  hidden?: boolean;
}

interface SettingsSidebarProps {
  groups: SidebarGroup[];
  standaloneItems: SidebarStandaloneItem[];
  activeGroupIndex: number;
  activeSectionId: string | null;
  onGroupClick: (groupIndex: number) => void;
  onSectionClick: (sectionId: string, groupIndex: number) => void;
  onStandaloneClick: (itemId: string) => void;
}

export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  groups,
  standaloneItems,
  activeGroupIndex,
  activeSectionId,
  onGroupClick,
  onSectionClick,
  onStandaloneClick,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const visibleGroups = groups.filter((g) => !g.hidden);
  const visibleStandalone = standaloneItems.filter((item) => !item.hidden);

  const closeDrawerIfMobile = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleSectionClick = (sectionId: string, groupIndex: number) => {
    onSectionClick(sectionId, groupIndex);
    closeDrawerIfMobile();
  };

  const handleGroupClick = (groupIndex: number) => {
    onGroupClick(groupIndex);
    closeDrawerIfMobile();
  };

  const handleStandaloneClick = (itemId: string) => {
    onStandaloneClick(itemId);
    closeDrawerIfMobile();
  };

  const renderSidebarContent = () => (
    <List sx={{ px: 0, py: 1 }}>
      {visibleGroups.map((group, groupIndex) => {
        const isActiveGroup = activeGroupIndex === groupIndex;

        return (
          <React.Fragment key={group.label}>
            {/* Group Header */}
            <ListItem
              component="button"
              onClick={() => handleGroupClick(groupIndex)}
              sx={{
                py: 1,
                px: 2,
                cursor: 'pointer',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemText
                primary={group.label}
                primaryTypographyProps={{
                  variant: 'overline',
                  fontWeight: 700,
                  color: isActiveGroup ? 'primary.main' : 'text.secondary',
                  letterSpacing: 1.2,
                }}
              />
            </ListItem>

            {/* Section Items */}
            {group.items.map((item) => {
              const isActive = isActiveGroup && activeSectionId === item.id;

              return (
                <ListItem
                  key={item.id}
                  component="button"
                  onClick={() => handleSectionClick(item.id, groupIndex)}
                  sx={{
                    py: 1,
                    pl: 3,
                    pr: 2,
                    borderLeft: isActive
                      ? `4px solid ${theme.palette.primary.main}`
                      : '4px solid transparent',
                    backgroundColor: isActive
                      ? `${theme.palette.primary.main}14`
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive
                        ? `${theme.palette.primary.main}1F`
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                    transition: theme.transitions.create(
                      ['background-color', 'border-left'],
                      { duration: theme.transitions.duration.short }
                    ),
                    cursor: 'pointer',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'primary.main' : 'text.primary',
                    }}
                  />
                </ListItem>
              );
            })}
          </React.Fragment>
        );
      })}

      {/* Divider before standalone items */}
      {visibleStandalone.length > 0 && <Divider sx={{ my: 1 }} />}

      {/* Standalone Items */}
      {visibleStandalone.map((item) => (
        <ListItem
          key={item.id}
          component="button"
          onClick={() => handleStandaloneClick(item.id)}
          sx={{
            py: 1,
            px: 2,
            cursor: 'pointer',
            border: 'none',
            width: '100%',
            textAlign: 'left',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              variant: 'body2',
              fontWeight: 400,
              color: 'text.primary',
            }}
          />
        </ListItem>
      ))}
    </List>
  );

  return (
    <>
      {/* Mobile: FAB to open drawer */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="Open settings menu"
          onClick={() => setMobileOpen(true)}
          variant="extended"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: theme.zIndex.speedDial,
            gap: 0.75,
          }}
          size="medium"
        >
          <ListAltIcon />
          menu
        </Fab>
      )}

      {/* Mobile: Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Settings
            </Typography>
          </Box>
          {renderSidebarContent()}
        </Drawer>
      )}

      {/* Desktop: Sidebar content */}
      {!isMobile && (
        <Paper elevation={1} sx={{ overflow: 'auto' }}>
          {renderSidebarContent()}
        </Paper>
      )}
    </>
  );
};
